import type { BlockComponentProps } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import type { SyntheticEvent, ReactElement } from 'react';
import { useCallback, useMemo, memo } from 'react';
import type { ImageComponentType, ImageBlockStateType } from '@unifyapps/defs/blocks/Image/types';
import useBlockEvents from '@unifyapps/carbon/no-code/hooks/useBlockEvents';
import { useBlockAppearanceStyles } from '@unifyapps/carbon/no-code/hooks/useBlockAppearanceStyles';
import MediaViewer from '@unifyapps/carbon/components/MediaViewer';
import { clsx } from 'clsx';
import { EventTypeEnum } from '@unifyapps/defs/types/event';
import _some from 'lodash/some';
import _set from 'lodash/set';
import { Tooltip } from '@unifyapps/ui/components/Tooltip';
import type { TooltipPlacement } from '@unifyapps/defs/blocks/Typography/types';
import { Image as BaseImage } from '../common/Image';

function WithTooltip({
  text,
  placement,
  width,
  children,
}: {
  text: string;
  placement: TooltipPlacement;
  width: string | { custom: string } | undefined;
  children: ReactElement;
}) {
  const { className: tooltipClassName, style: tooltipStyle } = useBlockAppearanceStyles({
    appearanceStyles: { width },
  });

  const slotProps = useMemo(
    () => ({
      title: {
        className: clsx('p-xxs whitespace-pre-line', tooltipClassName),
        style: tooltipStyle,
      },
    }),
    [tooltipClassName, tooltipStyle],
  );

  return (
    <Tooltip placement={placement} slotProps={slotProps} title={text}>
      {children}
    </Tooltip>
  );
}

function Image({
  attributesAndEventListeners,
  events,
  computedBlockState,
  updateBlockState,
  dndRef,
}: BlockComponentProps<ImageComponentType, ImageBlockStateType, ImageBlockStateType>) {
  const { content, appearance } = computedBlockState;
  const { src, alt, addOns } = content;

  const { isOpenInMediaViewer } = computedBlockState.state;

  const { emitEvent } = useBlockEvents(events, computedBlockState.id);

  const isClickEventPresent = useMemo(
    () => _some(events, { eventType: EventTypeEnum.OnClick }),
    [events],
  );

  const onClick = useCallback(
    (e: SyntheticEvent) => {
      void emitEvent({ eventType: EventTypeEnum.OnClick, domEvent: e });
    },
    [emitEvent],
  );

  const onClose = useCallback(() => {
    updateBlockState((draft) => {
      _set(draft, 'state.isOpenInMediaViewer', false);
    });
  }, [updateBlockState]);

  const adaptedMedia = useMemo(() => {
    return [{ url: src, id: src, mimeType: 'image' }];
  }, [src]);

  const contentEl = (
    <BaseImage
      attributesAndEventListeners={attributesAndEventListeners}
      onClick={onClick}
      ref={isOpenInMediaViewer ? null : dndRef}
      hasClickEvent={isClickEventPresent}
      src={src}
      alt={alt}
      styles={appearance?.styles}
      objectFit={appearance?.objectFit}
    />
  );

  const mediaViewerEl = isOpenInMediaViewer ? (
    <MediaViewer medias={adaptedMedia} onClose={onClose} />
  ) : null;

  if (!addOns?.tooltip?.text) {
    return (
      <>
        {contentEl}
        {mediaViewerEl}
      </>
    );
  }

  return (
    <>
      <WithTooltip
        placement={addOns.tooltip.placement ?? 'top'}
        text={addOns.tooltip.text}
        width={addOns.tooltip.width}
      >
        {contentEl}
      </WithTooltip>
      {mediaViewerEl}
    </>
  );
}

export default memo(Image);
